import { Button } from '@1trading/react/components/ui/button';
import { GradientIcon } from '@1trading/react/components/ui/gradient-icon';
import { CircleCheck, CircleX, FileClock } from 'lucide-react';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useNavigate } from 'react-router-dom';

import { PageHeading, PageHeadingDescription, PageHeadingTitle } from '@/components/ui/page-heading';
import { InstitutionFuturesProgressStep } from '@/context/progress';
import FuturesAppropriatenessTestLayout from '@/features/futures/layouts/appropriateness-test';
import { useProfileClientCategorisationTreatment } from '@/hooks/queries/use-profile-client-categorisation-treatment';
import { useProfileFuturesAppropriateness } from '@/hooks/queries/use-profile-futures-appropriateness';
import { useProfileInstitutionDetailsMeta } from '@/hooks/queries/use-profile-institution-details';
import LoadingPage from '@/pages/loading';
import { FuturesAppropriatenessResultStatus } from '@/services/api/profile/futures-appropriateness';

const ID = 'institution-onboarding-futures-result-page';

const APPROPRIATENESS_TEST_ROUTE = '/institution/onboarding/futures/appropriateness-test';

// Every outcome continues the application: futures follows the result, while spot doesn't depend on it
const InstitutionOnboardingFuturesAppropriatenessTestResultContinueButton = () => {
  const { t } = useTranslation();

  return (
    <Button size="lg" variant="primary" asChild>
      <Link to="/institution/onboarding/verification" replace>
        {t('institutionDetails.futuresOnboarding.continue')}
      </Link>
    </Button>
  );
};
InstitutionOnboardingFuturesAppropriatenessTestResultContinueButton.displayName = 'InstitutionOnboardingFuturesAppropriatenessTestResultContinueButton';

const InstitutionOnboardingFuturesAppropriatenessTestResultPassed = () => {
  const { t } = useTranslation();

  return (
    <>
      <GradientIcon className="size-32 mx-auto" strokeWidth={1.5}>
        <CircleCheck />
      </GradientIcon>
      <PageHeading centered>
        <PageHeadingTitle>{t('institutionDetails.futuresOnboarding.passed.title')}</PageHeadingTitle>
        <PageHeadingDescription>{t('institutionDetails.futuresOnboarding.passed.description')}</PageHeadingDescription>
      </PageHeading>
      <InstitutionOnboardingFuturesAppropriatenessTestResultContinueButton />
    </>
  );
};
InstitutionOnboardingFuturesAppropriatenessTestResultPassed.displayName = 'InstitutionOnboardingFuturesAppropriatenessTestResultPassed';

const InstitutionOnboardingFuturesAppropriatenessTestResultManualReview = () => {
  const { t } = useTranslation();

  return (
    <>
      <GradientIcon className="size-32 mx-auto" strokeWidth={1.5}>
        <FileClock />
      </GradientIcon>
      <PageHeading centered>
        <PageHeadingTitle>{t('FuturesAppropriatenessAssessment.result.manualReview.title')}</PageHeadingTitle>
        <PageHeadingDescription>{t('FuturesAppropriatenessAssessment.result.manualReview.description.underReview')}</PageHeadingDescription>
        <PageHeadingDescription>{t('institutionDetails.futuresOnboarding.continueWhileReviewed')}</PageHeadingDescription>
      </PageHeading>
      <InstitutionOnboardingFuturesAppropriatenessTestResultContinueButton />
    </>
  );
};
InstitutionOnboardingFuturesAppropriatenessTestResultManualReview.displayName = 'InstitutionOnboardingFuturesAppropriatenessTestResultManualReview';

const InstitutionOnboardingFuturesAppropriatenessTestResultFailed = ({ canRetake }: { canRetake: boolean }) => {
  const { reset: resetFuturesAppropriateness } = useProfileFuturesAppropriateness();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleRetakeImmediately = useCallback(() => {
    resetFuturesAppropriateness();
    navigate(`${APPROPRIATENESS_TEST_ROUTE}?retake=true`, { replace: true });
  }, [navigate, resetFuturesAppropriateness]);

  return (
    <>
      <GradientIcon className="size-32 mx-auto" strokeWidth={1.5}>
        <CircleX />
      </GradientIcon>
      <PageHeading centered>
        <PageHeadingTitle>
          {canRetake ? t('FuturesAppropriatenessAssessment.result.failed.title') : t('FuturesAppropriatenessAssessment.result.failed.limitReached.title')}
        </PageHeadingTitle>
        <PageHeadingDescription>
          {canRetake ? t('FuturesAppropriatenessAssessment.result.failed.description') : t('FuturesAppropriatenessAssessment.result.failed.limitReached.description')}
        </PageHeadingDescription>
        <PageHeadingDescription>{t('institutionDetails.futuresOnboarding.continueWithoutFutures')}</PageHeadingDescription>
      </PageHeading>
      <footer className="flex flex-col gap-4">
        <InstitutionOnboardingFuturesAppropriatenessTestResultContinueButton />
        {canRetake ? (
          <Button size="lg" variant="outline" onClick={handleRetakeImmediately}>
            {t('FuturesAppropriatenessAssessment.result.failed.actions.retakeImmediately')}
          </Button>
        ) : null}
      </footer>
    </>
  );
};
InstitutionOnboardingFuturesAppropriatenessTestResultFailed.displayName = 'InstitutionOnboardingFuturesAppropriatenessTestResultFailed';

const InstitutionOnboardingFuturesAppropriatenessTestResultPage = () => {
  const {
    isLoading: isLoadingInstitutionDetailsMeta,
    data: { isRegulated },
  } = useProfileInstitutionDetailsMeta();
  const { isLoading: isLoadingClientCategorisationTreatment, data: clientCategorisationTreatment } = useProfileClientCategorisationTreatment();
  const { isLoading: isLoadingFuturesAppropriateness, data: futuresAppropriateness } = useProfileFuturesAppropriateness();
  const isLoading = isLoadingInstitutionDetailsMeta || isLoadingClientCategorisationTreatment || isLoadingFuturesAppropriateness;

  if (isLoading) return <LoadingPage />;

  if (!clientCategorisationTreatment) return <Navigate to="/institution/onboarding/futures/client-categorisation/treatment" replace />;

  // Regulated institutions don't take the appropriateness test
  const result = isRegulated
    ? { status: FuturesAppropriatenessResultStatus.PASSED, canRetake: false }
    : futuresAppropriateness && 'result' in futuresAppropriateness
      ? futuresAppropriateness.result
      : null;

  if (!result) return <Navigate to={APPROPRIATENESS_TEST_ROUTE} replace />;

  return (
    <FuturesAppropriatenessTestLayout className="-md:justify-center space-y-8" id={ID} progress={1} size="sm" step={InstitutionFuturesProgressStep.APPROPRIATENESS_TEST}>
      {
        {
          [FuturesAppropriatenessResultStatus.PASSED]: <InstitutionOnboardingFuturesAppropriatenessTestResultPassed />,
          [FuturesAppropriatenessResultStatus.MANUAL_REVIEW]: <InstitutionOnboardingFuturesAppropriatenessTestResultManualReview />,
          [FuturesAppropriatenessResultStatus.FAILED]: <InstitutionOnboardingFuturesAppropriatenessTestResultFailed canRetake={'canRetake' in result && !!result.canRetake} />,
        }[result.status]
      }
    </FuturesAppropriatenessTestLayout>
  );
};
InstitutionOnboardingFuturesAppropriatenessTestResultPage.displayName = 'InstitutionOnboardingFuturesAppropriatenessTestResultPage';

export default InstitutionOnboardingFuturesAppropriatenessTestResultPage;
